import { COMMAND_META } from '@/lib/command-meta';
import { useHelpModalStore } from '@/lib/help-modal-store';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@iq/ui';
import { useTranslation } from 'react-i18next';

/**
 * `/help` modal — lists every slash-command in the palette grouped by
 * kind (Tools vs Actions) with icon + formatted name + description.
 * Mirrors the screenshot pattern the user pointed to. Triggered by
 * picking `/help` in the composer palette; backed by `useHelpModalStore`
 * so the trigger lives outside the modal subtree.
 */
export function HelpModal() {
  const { t } = useTranslation();
  const open = useHelpModalStore((s) => s.open);
  const setOpen = useHelpModalStore((s) => s.setOpen);

  const tools = COMMAND_META.filter((m) => m.kind === 'tool');
  const actions = COMMAND_META.filter((m) => m.kind === 'action');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="h-[min(640px,calc(100dvh-2rem))] w-[calc(100vw-1rem)] max-w-md rounded-2xl gap-4 overflow-hidden p-0 sm:p-0"
        data-testid="help-modal"
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>{t('chat.commands.help.label')}</DialogTitle>
          <DialogDescription>{t('chat.commands.help.description')}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <section className="mb-4">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('chat.commands.section.tools')}
            </h3>
            <p
              className="mb-2 text-xs text-muted-foreground"
              data-testid="help-modal-tools-caption"
            >
              {t('chat.commands.section.toolsCaption')}
            </p>
            <ul className="flex flex-col gap-2">
              {tools.map((m) => {
                const Icon = m.icon;
                return (
                  <li
                    key={m.id}
                    className="flex items-start gap-3 rounded-lg border bg-background p-3"
                  >
                    <Icon className="size-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-medium">/{m.id}</span>
                        <span className="text-sm text-foreground">{t(m.labelKey)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{t(m.descriptionKey)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('chat.commands.section.actions')}
            </h3>
            <p
              className="mb-2 text-xs text-muted-foreground"
              data-testid="help-modal-actions-caption"
            >
              {t('chat.commands.section.actionsCaption')}
            </p>
            <ul className="flex flex-col gap-2">
              {actions.map((m) => {
                const Icon = m.icon;
                return (
                  <li
                    key={m.id}
                    className="flex items-start gap-3 rounded-lg border bg-background p-3"
                  >
                    <Icon className="size-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-medium">/{m.id}</span>
                        <span className="text-sm text-foreground">{t(m.labelKey)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{t(m.descriptionKey)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
